import type { ReactNode } from 'react'
import { IconChevronRight, IconRefresh, IconX } from './icons'

/**
 * Сгруппированный список iOS: карточка с большим радиусом, строки внутри,
 * разделители начинаются от текста, а не от края. Это главный строительный
 * блок всех экранов приложения «Фитнес».
 */
export function Group({
  caption,
  footnote,
  children,
}: {
  caption?: string
  footnote?: string
  children: ReactNode
}) {
  return (
    <section>
      {caption && <h2 className="group-caption mb-2 px-4">{caption}</h2>}
      <div className="row-divider overflow-hidden rounded-card bg-ink-surface">{children}</div>
      {footnote && <p className="footnote mt-2 px-4">{footnote}</p>}
    </section>
  )
}

/** Строка списка. С onPress ведёт себя как ячейка iOS: подсветка и шеврон. */
export function Row({
  title,
  subtitle,
  value,
  accessory,
  onPress,
  tone,
}: {
  title: ReactNode
  subtitle?: ReactNode
  value?: ReactNode
  accessory?: ReactNode
  onPress?: () => void
  tone?: 'default' | 'destructive'
}) {
  const body = (
    <>
      <span className="min-w-0 flex-1">
        <span className={`block ${tone === 'destructive' ? 'text-sys-red' : ''}`}>{title}</span>
        {subtitle && <span className="footnote mt-0.5 block">{subtitle}</span>}
      </span>
      {value && <span className="numerals shrink-0 text-ink-2">{value}</span>}
      {accessory}
      {onPress && !accessory && <IconChevronRight size={17} className="shrink-0 text-ink-3" />}
    </>
  )

  const shape = 'flex min-h-11 w-full items-center gap-3 px-4 py-2.5 text-left text-[17px]'

  if (!onPress) return <div className={shape}>{body}</div>
  return (
    <button type="button" onClick={onPress} className={`${shape} active:bg-ink-surface2`}>
      {body}
    </button>
  )
}

/**
 * Кнопка iOS. Filled — заполненная цветом, tinted — цвет на подложке,
 * plain — только текст. Высота 44 точки, ниже неё палец начинает промахиваться.
 */
export function Button({
  children,
  onClick,
  variant = 'tinted',
  disabled,
  full,
  color,
}: {
  children: ReactNode
  onClick?: () => void
  variant?: 'filled' | 'tinted' | 'plain'
  disabled?: boolean
  full?: boolean
  color?: string
}) {
  const accent = color ?? 'var(--color-sys-blue)'
  const base =
    'inline-flex min-h-11 items-center justify-center gap-1.5 rounded-[12px] px-4 text-[17px] font-medium transition-opacity disabled:opacity-30 active:opacity-60'

  const style =
    variant === 'filled'
      ? { background: accent, color: '#fff' }
      : variant === 'tinted'
        ? { background: 'color-mix(in srgb, ' + accent + ' 18%, transparent)', color: accent }
        : { color: accent }

  return (
    <button
      type="button"
      style={style}
      className={`${base} ${full ? 'w-full' : ''}`}
      onClick={onClick}
      disabled={disabled}
    >
      {children}
    </button>
  )
}

export function Screen({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="pb-nav flex flex-col gap-6 px-4 pt-3">
      <h1 className="title-large">{title}</h1>
      {children}
    </div>
  )
}

export function Empty({ children }: { children: ReactNode }) {
  return <p className="footnote px-4 py-3">{children}</p>
}

export function Spinner({ label }: { label: string }) {
  return (
    <div className="flex h-full items-center justify-center p-6 text-center text-[15px] text-ink-2">
      {label}
    </div>
  )
}

/** Экран не загрузился: причина и выход, а не вечное «Загружаю…». */
export function LoadError({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div className="flex flex-col items-center gap-4 p-8 text-center">
      <p className="max-w-xs text-[15px] text-ink-2">{message}</p>
      <Button onClick={onRetry}>
        <IconRefresh size={17} />
        Повторить
      </Button>
    </div>
  )
}

export function ErrorBanner({ message, onClose }: { message: string; onClose?: () => void }) {
  return (
    <div className="flex items-start gap-3 rounded-card bg-sys-red/15 px-4 py-3 text-[15px] text-sys-red">
      <span className="min-w-0 flex-1">{message}</span>
      {onClose && (
        <button type="button" onClick={onClose} className="shrink-0 p-1" aria-label="Скрыть">
          <IconX size={15} />
        </button>
      )}
    </div>
  )
}
